import React, { useMemo, useCallback, useRef } from 'react';
import { Handle, Position, useStore } from 'reactflow';
import useSyncNodeInternals from '../Components/useSyncNodeInternals';

import { FiMessageSquare } from 'react-icons/fi';
import { BsLink45Deg, BsPersonVcard } from 'react-icons/bs';
import { FaLocationDot } from 'react-icons/fa6';
import NodeCardShell from '../Components/NodeCardShell';
import { getCardContent } from '../Components/nodeSummary';
import NodeOutputRow, { NodeOutputGroup, NodeStaticRow } from '../Components/NodeOutputRow';

const BASIC_ANSWER_NODE_COLOR = '#00A884';

const CustomNode = ({ data, isConnectable, id, selected }) => {
  const ownButtonIds = useMemo(
    () => [
      ...(data.options || []).map((b) => b.id),
      ...(data?.advanced?.campaign_options || []).map((b) => b.id),
    ],
    [data.options, data?.advanced?.campaign_options]
  );

  const handlePrefixes = useMemo(
    () => ownButtonIds.map((bid) => `source-${bid}-`),
    [ownButtonIds]
  );

  // El selector corre en CADA cambio del store (cada nodo medido, cada arrastre).
  // Sin caché, con cientos de nodos eran millones de vueltas al abrir el bot;
  // solo se recalcula cuando cambia el arreglo de edges.
  const selectorCache = useRef({ edges: null, prefixes: null, value: '' });
  const selectConnectedHandles = useCallback(
    (state) => {
      if (!handlePrefixes.length) return '';
      const cache = selectorCache.current;
      if (cache.edges === state.edges && cache.prefixes === handlePrefixes) {
        return cache.value;
      }
      let matches = '';
      for (const edge of state.edges) {
        const handle = edge.sourceHandle;
        if (!handle) continue;
        for (let i = 0; i < handlePrefixes.length; i++) {
          if (handle.startsWith(handlePrefixes[i])) {
            matches = matches ? `${matches}|${handle}` : handle;
            break;
          }
        }
      }
      selectorCache.current = { edges: state.edges, prefixes: handlePrefixes, value: matches };
      return matches;
    },
    [handlePrefixes]
  );

  const connectedHandles = useStore(selectConnectedHandles);
  const connectedHandlesList = useMemo(
    () => (connectedHandles ? connectedHandles.split('|') : []),
    [connectedHandles]
  );

  const handlesSignature = [
    data.first ? 'f' : '',
    data.saved ? 's' : '',
    ownButtonIds.join(','),
    connectedHandles,
  ].join('~');

  useSyncNodeInternals(id, handlesSignature);

  const messageText = getCardContent({ type: 'basicAnswer', data });
  const advanced = data?.advanced || {};
  const staticButtons = [
    advanced.location && { key: 'location', icon: FaLocationDot, label: 'Enviar ubicación' },
    advanced.request_contact && { key: 'contact', icon: BsPersonVcard, label: 'Compartir número' },
    advanced.url_button && { key: 'url', icon: BsLink45Deg, label: advanced.url_button_text || 'Abrir enlace' },
  ].filter(Boolean);

  return (
    <NodeCardShell
      icon={FiMessageSquare}
      color={BASIC_ANSWER_NODE_COLOR}
      typeName="Respuesta Básica"
      title={data.title ?? 'Respuesta Básica'}
      summary={messageText}
      trigger={data.user_response}
      error={data?.error}
      selected={selected}
      first={data.first}
      handles={
        <>
          {!data.first && data.saved && (
            <Handle
              type="target"
              position={Position.Top}
              isConnectable={isConnectable}
              id={`target-${id}`}
              style={{ backgroundColor: BASIC_ANSWER_NODE_COLOR }}
            />
          )}
          {data.saved && (
            <Handle
              type="target"
              position={Position.Left}
              isConnectable={isConnectable}
              id={`target-return-${id}`}
              style={{ backgroundColor: BASIC_ANSWER_NODE_COLOR }}
            />
          )}
          {data.saved && (
            <Handle
              type="source"
              position={Position.Bottom}
              id={`source-${id}`}
              isConnectable={isConnectable}
              style={{ backgroundColor: BASIC_ANSWER_NODE_COLOR }}
            />
          )}
        </>
      }
    >
      {(data.options?.length > 0 || data?.advanced?.campaign_options?.length > 0) && (
        <NodeOutputGroup>
          {data.options?.map((btns, index) => {
              const existingHandle = connectedHandlesList.find((sourceHandle) =>
                sourceHandle.startsWith(`source-${btns.id}-`)
              );
              const handleId = existingHandle || `source-${btns.id}`;
              return (
                <NodeOutputRow
                  key={index}
                  label={btns.label}
                  color={BASIC_ANSWER_NODE_COLOR}
                  handleId={handleId}
                  isConnectable={isConnectable}
                />
              );
            })}
            {data?.advanced?.campaign_options?.map((btns, index) => {
              const existingHandle = connectedHandlesList.find((sourceHandle) =>
                sourceHandle.startsWith(`source-${btns.id}-`)
              );
              const handleId = existingHandle || `source-${btns.id}`;
              return (
                <NodeOutputRow
                  key={`campaign-${index}`}
                  label={btns.label}
                  color={BASIC_ANSWER_NODE_COLOR}
                  handleId={handleId}
                  isConnectable={isConnectable}
                />
              );
            })}
        </NodeOutputGroup>
      )}
      {staticButtons.length > 0 && (
        <NodeOutputGroup>
          {staticButtons.map((btn) => (
            <NodeStaticRow
              key={btn.key}
              icon={btn.icon}
              label={btn.label}
              color={BASIC_ANSWER_NODE_COLOR}
            />
          ))}
        </NodeOutputGroup>
      )}
    </NodeCardShell>
  );
};

export default React.memo(CustomNode);
